/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import LinearProgressDisplay from '@brands/Components/ProgressDisplay/ProgressDisplay';
import { medicalConditionQuestions } from '@brands/Dashboard/MedicalHistory/MedicalCondition/utils/questions';
import { MedicalConditionSubmit } from '@brands/Dashboard/MedicalHistory/MedicalCondition/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import QuestionAndRadioButton from '../../../../../Components/QuestionAndAnswers/QuestionAndRadioButtonForMed/QuestionAndRadioButton';
import closeIcon from '../Icons/selectPatientClose.svg';
import logo from '../Icons/selectPatientLogo.svg';
import styles from './styles.module.scss';
import { validationSchema } from './utils/validationSchema';

const ClinicalDataStepOne = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { state } = useLocation();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [conditionsObjectId, setConditionsObjectId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
    trigger,
    reset,
  } = useForm<MedicalConditionSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: MedicalConditionSubmit): Promise<void> => {
    try {
      setLoading(true);
      const {
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed,
        none_of_the_above,
      } = data;

      const medicalConditionPayload: MedicalConditionSubmit = {
        user_id: selectedPatient?.value as number,
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed: conditions_not_listed === 'false' ? '' : conditions_not_listed,
        none_of_the_above,
      };

      if (!conditionsObjectId) {
        await addClinicalData('medical_conditions', medicalConditionPayload);
      } else {
        await updateClinicalData('medical_conditions', conditionsObjectId.toString(), medicalConditionPayload);
      }

      reset(watch());
      showSnackbar('Medical conditions saved successfully!', 'success');
      setLoading(false);
      roleNavigate('/clinical-data/step-two', {
        state: {
          caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
          isPatientForm: true,
          shouldSeeLastModal: state || '',
          patientId: selectedPatient?.value ? selectedPatient?.value : '',
        },
      });
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    displayErrorDetails(e);
  };

  const backToDashboard = (): void => {
    roleNavigate('/dashboard');
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      const res = await listClinicalData(selectedPatient?.value);
      if (res.medical_conditions) {
        setConditionsObjectId(res.medical_conditions.id || null);
        setValue('arthritis', res.medical_conditions.arthritis || false, { shouldValidate: true });
        setValue('cancer', res.medical_conditions.cancer || false, { shouldValidate: true });
        setValue('dementia_alzheimers', res.medical_conditions.dementia_alzheimers || false, { shouldValidate: true });
        setValue('diabetes', res.medical_conditions.diabetes || false, { shouldValidate: true });
        setValue('heart_disease', res.medical_conditions.heart_disease || false, { shouldValidate: true });
        setValue('dry_mouth', res.medical_conditions.dry_mouth || false, { shouldValidate: true });
        setValue('obesity', res.medical_conditions.obesity || false, { shouldValidate: true });
        setValue('pregnancy_or_breastfeeding', res.medical_conditions.pregnancy_or_breastfeeding || false, {
          shouldValidate: true,
        });
        setValue('respiratory_conditions', res.medical_conditions.respiratory_conditions || false, {
          shouldValidate: true,
        });
        setValue('stroke', res.medical_conditions.stroke || false, { shouldValidate: true });
        setValue(
          'conditions_not_listed',
          res.medical_conditions.conditions_not_listed === 'true'
            ? ''
            : res.medical_conditions.conditions_not_listed || '',
          { shouldValidate: true },
        );
        setValue('none_of_the_above', res.medical_conditions.none_of_the_above || false, { shouldValidate: true });
      }

      trigger();

      setLoading(false);
    })();
  }, [selectedPatient, setValue]);

  useEffect(() => {
    const noneOfTheAbove = watch('none_of_the_above');
    if (noneOfTheAbove) {
      setValue('arthritis', false, { shouldValidate: true });
      setValue('cancer', false, { shouldValidate: true });
      setValue('dementia_alzheimers', false, { shouldValidate: true });
      setValue('diabetes', false, { shouldValidate: true });
      setValue('heart_disease', false, { shouldValidate: true });
      setValue('dry_mouth', false, { shouldValidate: true });
      setValue('obesity', false, { shouldValidate: true });
      setValue('pregnancy_or_breastfeeding', false, { shouldValidate: true });
      setValue('respiratory_conditions', false, { shouldValidate: true });
      setValue('stroke', false, { shouldValidate: true });
      setValue('conditions_not_listed', '', { shouldValidate: true });
    }
  }, [watch('none_of_the_above')]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <div className={styles.clinicalDataIntroTopDiv}>
          <div className={styles.clinicalDataIntroTopDivFirst}>
            <img src={logo} className={styles.clinicalDataIntroTopDivFirstLogo} alt="Logo" />
            <div onClick={backToDashboard}>
              <img src={closeIcon} className={styles.clinicalDataIntroTopDivFirstClose} alt="Close" />
            </div>
          </div>
          <LinearProgressDisplay progress={10} />
        </div>
        <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroStep}>
                <span>MEDICAL HISTORY: 1 OF 9</span>
              </div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>Do you have, or have you had, any of the following medical conditions?</span>
                <span className={styles.clinicalDataIntroTitleRequired}> *</span>
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                {[...medicalConditionQuestions].map((element) => (
                  <QuestionAndRadioButton<MedicalConditionSubmit>
                    {...register(element.id as keyof MedicalConditionSubmit)}
                    key={element.id}
                    element={element}
                    value={watch(element.id as keyof MedicalConditionSubmit)}
                    setValue={setValue}
                    trigger={trigger}
                  />
                ))}
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || !isValid}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepOne;
